import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  startActingOnBehalf,
  stopActingOnBehalf,
  validateSession,
  extendSession,
  isSessionExpired,
} from '../redux/actingOnBehalfSlice';
import { RootState } from '../redux/store';
import { FamilyMember, getMemberDisplayName } from '../types/family';

interface ActingOnBehalfContext {
  isActingOnBehalf: boolean;
  targetMember: {
    id: string;
    name: string;
  } | null;
  familyId: string | null;
  startedAt: string | null;
  expiresAt: string | null;
  startActing: (member: FamilyMember, familyId: string) => void;
  stopActing: () => void;
  canActOnBehalfOf: (memberId: string, familyMembers: FamilyMember[]) => boolean;
  extendCurrentSession: () => void;
}

export function useActingOnBehalf(): ActingOnBehalfContext {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const actingOnBehalfState = useSelector((state: RootState) => state.actingOnBehalf);
  const currentUserId = useSelector((state: RootState) => state.user.user_id);
  const userRole = useSelector((state: RootState) => state.user.role);

  // Validate session on mount and when state changes
  useEffect(() => {
    dispatch(validateSession());
  }, [dispatch]);

  // Check expiration periodically (every minute)
  useEffect(() => {
    if (!actingOnBehalfState.isActive) return;

    const checkInterval = setInterval(() => {
      if (isSessionExpired(actingOnBehalfState.expiresAt)) {
        dispatch(stopActingOnBehalf());
        navigate('/member/home');
      }
    }, 60000); // Check every minute

    return () => clearInterval(checkInterval);
  }, [actingOnBehalfState.isActive, actingOnBehalfState.expiresAt, dispatch, navigate]);

  // Compute isActingOnBehalf with expiration check
  const isActingOnBehalf =
    actingOnBehalfState.isActive && !isSessionExpired(actingOnBehalfState.expiresAt);

  const targetMember = isActingOnBehalf
    ? {
        id: actingOnBehalfState.targetMemberId!,
        name: actingOnBehalfState.targetMemberName!,
      }
    : null;

  const startActing = useCallback(
    (member: FamilyMember, familyId: string) => {
      // Only CLIENT role users can act on behalf of family members
      if (userRole !== 'CLIENT') {
        console.warn('Only CLIENT users can act on behalf of family members');
        return;
      }

      const memberName = getMemberDisplayName(member);
      const memberId = member.profile?.client_id || member.id;

      dispatch(
        startActingOnBehalf({
          memberId,
          memberName,
          familyId,
        })
      );

      navigate(`/member/home/${memberId}`);
    },
    [dispatch, navigate, userRole]
  );

  const stopActing = useCallback(() => {
    dispatch(stopActingOnBehalf());
    navigate('/member/home');
  }, [dispatch, navigate]);

  const extendCurrentSession = useCallback(() => {
    if (isActingOnBehalf) {
      dispatch(extendSession());
    }
  }, [dispatch, isActingOnBehalf]);

  const canActOnBehalfOf = useCallback(
    (memberId: string, familyMembers: FamilyMember[]): boolean => {
      if (!currentUserId) return false;

      // Only CLIENT role can use this feature
      if (userRole !== 'CLIENT') return false;

      // Find current user in family members
      const currentUserMember = familyMembers.find(
        (m) => m.user === currentUserId || m.profile?.user_profile_id === currentUserId
      );

      // User must be family admin
      if (!currentUserMember || !currentUserMember.is_admin) return false;

      // Target must be in same family
      const targetMember = familyMembers.find(
        (m) => m.id === memberId || m.profile?.client_id === memberId
      );

      if (!targetMember) return false;

      // Cannot act on behalf of self
      if (targetMember.user === currentUserId) return false;

      return true;
    },
    [currentUserId, userRole]
  );

  return {
    isActingOnBehalf,
    targetMember,
    familyId: actingOnBehalfState.familyId,
    startedAt: actingOnBehalfState.startedAt,
    expiresAt: actingOnBehalfState.expiresAt,
    startActing,
    stopActing,
    canActOnBehalfOf,
    extendCurrentSession,
  };
}
